import request from "superagent";
import Auth from "../../routes/Auth";
import { getRequest } from "../../api/ApiService";

const API = process.env.REACT_APP_API_URL;
const DEFAULT_PASSWORD = "business101";
const MAX_TEAM_SIZE = 6;

const POLL_FIRST_DELAY_MS = 10000;
const POLL_MID_DELAY_MS = 30000;
const POLL_LATE_DELAY_MS = 60000;
const POLL_MID_TO_LATE_MS = 120000;
const POLL_MAX_DURATION_MS = 300000;

const sleep = (ms) => new Promise(res => setTimeout(res, ms));

// ---------- CSV parsing ----------

const parseCsvLine = (line) => {
  const cells = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else { inQuotes = false; }
      } else {
        cur += ch;
      }
    } else {
      if (ch === ",") { cells.push(cur); cur = ""; }
      else if (ch === '"') { inQuotes = true; }
      else { cur += ch; }
    }
  }
  cells.push(cur);
  return cells.map(c => c.trim());
};

const normalizeHeader = (h) => (h || "")
  .toLowerCase()
  .replace(/\(.*?\)/g, "")
  .replace(/[^a-z0-9]+/g, "")
  .trim();

const HEADER_ALIASES = {
  firstname: "first_name",
  lastname: "last_name",
  email: "email",
  team: "team",
  // Optional: if the instructor includes a Competition column, we
  // honor it per team (see mergeCompetitionAssignments below).
  // Missing / blank rows fall back to the auto-assign algorithm.
  competition: "competition",
  // Optional: instructor-supplied login username. Blank cells fall
  // back to the row's email in buildStudentCsv. Collisions (within
  // the file or against existing course members) are blocked in
  // validateRoster / runRosterUpload.
  username: "username",
};

const readFileAsText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result || "");
  reader.onerror = () => reject(new Error("Could not read the uploaded file."));
  reader.readAsText(file);
});

export const parseRosterCsv = (text) => {
  const lines = String(text || "")
    .replace(/^﻿/, "")
    .split(/\r?\n/)
    .filter(l => l.trim().length > 0);

  if (lines.length < 1) {
    return { rows: [], headers: [], error: "The uploaded file is empty." };
  }

  const rawHeaders = parseCsvLine(lines[0]);
  const headerMap = rawHeaders.map(h => HEADER_ALIASES[normalizeHeader(h)] || null);

  const requiredFields = ["first_name", "last_name", "email", "team"];
  const missingHeaders = requiredFields.filter(rf => !headerMap.includes(rf));
  if (missingHeaders.length) {
    return {
      rows: [],
      headers: rawHeaders,
      error: `The uploaded file is missing required columns: ${missingHeaders.join(", ")}.`,
    };
  }

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    const row = { _rowNumber: i + 1 };
    headerMap.forEach((field, idx) => {
      if (field) row[field] = (cells[idx] || "").trim();
    });
    rows.push(row);
  }

  return { rows, headers: rawHeaders, error: null };
};

// ---------- Validation ----------

export const validateRoster = (rows) => {
  const errors = [];
  const requiredFields = [
    { key: "first_name", label: "First Name" },
    { key: "last_name", label: "Last Name" },
    { key: "email", label: "Email" },
    { key: "team", label: "Team" },
  ];

  const missingByRow = {};
  rows.forEach(row => {
    const missing = requiredFields
      .filter(f => !row[f.key])
      .map(f => f.label);
    if (missing.length) {
      missingByRow[row._rowNumber] = missing;
    }
  });
  Object.keys(missingByRow).forEach(rowNum => {
    errors.push(`Row ${rowNum} is missing: ${missingByRow[rowNum].join(", ")}.`);
  });

  const emailCounts = {};
  rows.forEach(row => {
    const e = (row.email || "").toLowerCase();
    if (!e) return;
    if (!emailCounts[e]) emailCounts[e] = [];
    emailCounts[e].push(row._rowNumber);
  });
  Object.keys(emailCounts).forEach(email => {
    if (emailCounts[email].length > 1) {
      errors.push(`Email "${email}" appears on rows ${emailCounts[email].join(", ")}.`);
    }
  });

  // Catch intra-file username collisions. Blank cells are allowed
  // (those rows fall back to email in buildStudentCsv). The
  // runRosterUpload cross-check against existing course members
  // handles collisions with already-registered users.
  const usernameCounts = {};
  rows.forEach(row => {
    const u = (row.username || "").toLowerCase();
    if (!u) return;
    if (!usernameCounts[u]) usernameCounts[u] = [];
    usernameCounts[u].push(row._rowNumber);
  });
  Object.keys(usernameCounts).forEach(u => {
    if (usernameCounts[u].length > 1) {
      errors.push(`Username "${u}" appears on rows ${usernameCounts[u].join(", ")}.`);
    }
  });

  const teamCounts = {};
  rows.forEach(row => {
    const t = (row.team || "").trim();
    if (!t) return;
    if (!teamCounts[t]) teamCounts[t] = 0;
    teamCounts[t] += 1;
  });
  Object.keys(teamCounts).forEach(team => {
    if (teamCounts[team] > MAX_TEAM_SIZE) {
      errors.push(`Team "${team}" has ${teamCounts[team]} members. Max is ${MAX_TEAM_SIZE}.`);
    }
  });

  // If Competition is provided per row, values must be positive
  // integers and every row for the same team must agree. Blank cells
  // are allowed and fall back to auto-assignment downstream.
  const competitionByTeam = {};
  rows.forEach(row => {
    const t = (row.team || "").trim();
    const c = (row.competition || "").trim();
    if (!t || !c) return;
    if (!/^\d+$/.test(c) || Number(c) < 1) {
      errors.push(`Row ${row._rowNumber} has an invalid Competition value "${c}". Use a positive integer.`);
      return;
    }
    if (competitionByTeam[t] == null) {
      competitionByTeam[t] = c;
    } else if (competitionByTeam[t] !== c) {
      errors.push(
        `Team "${t}" has conflicting Competition values (${competitionByTeam[t]} vs ${c}). All rows for a team must use the same Competition.`
      );
    }
  });

  return { errors };
};

// ---------- Competition assignment ----------

export const determineCompetitions = (teamNames) => {
  const N = teamNames.length;
  if (N === 0) return { numCompetitions: 0, assignments: {}, sizes: [] };

  const numCompetitions = Math.max(1, Math.round(N / 7));
  const baseSize = Math.floor(N / numCompetitions);
  const remainder = N % numCompetitions;

  const sizes = [];
  for (let i = 0; i < numCompetitions; i++) {
    sizes.push(baseSize + (i < remainder ? 1 : 0));
  }

  const assignments = {};
  let idx = 0;
  for (let c = 0; c < numCompetitions; c++) {
    for (let j = 0; j < sizes[c]; j++) {
      assignments[teamNames[idx]] = c + 1;
      idx += 1;
    }
  }

  return { numCompetitions, assignments, sizes };
};

// ---------- CSV builders ----------

const csvEscape = (val) => {
  const s = String(val == null ? "" : val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const buildCsv = (headers, rows) => {
  const headerLine = headers.map(csvEscape).join(",");
  const bodyLines = rows.map(r => headers.map(h => csvEscape(r[h])).join(","));
  return [headerLine, ...bodyLines].join("\n") + "\n";
};

export const buildStudentCsv = (rows) => {
  const outRows = rows.map(r => {
    const explicitUsername = (r.username || "").trim();
    return {
      username: explicitUsername || r.email,
      first_name: r.first_name,
      last_name: r.last_name,
      email: r.email,
      password: DEFAULT_PASSWORD,
    };
  });
  return buildCsv(["username", "first_name", "last_name", "email", "password"], outRows);
};

// Existing teams keep their BE-side competition assignment: the
// `competition_id` column carries the DB PK, and BE reuses it as-is.
// New teams get a fresh competition NAME (small integer) in the
// `competition` column; BE creates the competition if it doesn't
// exist yet. Exactly one of the two columns is populated per row.
export const buildGameCsv = (rows, roleByEmail, competitionByTeam, competitionIdByTeam) => {
  const outRows = rows.map(r => {
    const emailKey = (r.email || "").toLowerCase();
    const existingRole = roleByEmail[emailKey];
    const explicitUsername = (r.username || "").trim();
    return {
      username: explicitUsername || r.email,
      competition: competitionByTeam[r.team] != null ? String(competitionByTeam[r.team]) : "",
      competition_id: competitionIdByTeam[r.team] != null ? String(competitionIdByTeam[r.team]) : "",
      team: r.team,
      role: existingRole || "CEO",
    };
  });
  return buildCsv(["username", "competition", "competition_id", "team", "role"], outRows);
};

// ---------- HTTP helpers ----------

const authHeader = () => {
  const token = Auth.getToken();
  return token ? { Authorization: `Token ${token}` } : {};
};

const postFile = (url, csv, filename, extraFields) => {
  const req = request
    .post(url)
    .set("Accept", "application/json")
    .set(authHeader())
    .attach("file", new Blob([csv], { type: "text/csv" }), filename);

  if (extraFields) {
    Object.keys(extraFields).forEach(k => {
      req.field(k, String(extraFields[k]));
    });
  }
  return req;
};

const fetchAllPages = async (url) => {
  let next = url;
  const all = [];
  while (next) {
    const res = await getRequest(next);
    const body = res.body || {};
    if (Array.isArray(body)) {
      all.push(...body);
      next = null;
    } else {
      all.push(...(body.results || []));
      next = body.next;
    }
  }
  return all;
};

// Returns the key the BE uses to identify a coursemember's user.
// /v2/games/coursemembers/ serializes `user` as the username STRING
// (e.g. "colm-janotf26"), not a nested user object. Older/alternate
// shapes (nested object, bare `username`, bare `email`) still fall
// through below. We match this against the resolved username we POST
// (CSV username || email), so re-uploads skip rows already in the
// course regardless of whether the first upload used an explicit
// username or let it default to email.
const extractCourseMemberUserKey = (cm) => {
  if (!cm) return "";
  if (typeof cm.user === "string") return cm.user;
  if (cm.user) {
    if (cm.user.username) return cm.user.username;
    if (cm.user.email) return cm.user.email;
  }
  if (cm.username) return cm.username;
  if (cm.email) return cm.email;
  return "";
};

const extractTeamPlayerEmail = (tp) => {
  if (!tp || !tp.user) return "";
  return tp.user.email || tp.user.username || "";
};

// ---------- Poll ----------

const pollCourseMembers = async ({ courseId, expectedUserKeys, onProgress }) => {
  const startTime = Date.now();
  const expectedSet = new Set(expectedUserKeys.map(k => (k || "").toLowerCase()));

  const runCheck = async () => {
    const members = await fetchAllPages(`${API}v2/games/coursemembers/?page_size=500`);
    const found = new Set(
      members
        .map(m => extractCourseMemberUserKey(m).toLowerCase())
        .filter(Boolean)
    );
    let matched = 0;
    expectedSet.forEach(k => { if (found.has(k)) matched += 1; });
    if (onProgress) onProgress({ matched, total: expectedSet.size });
    return matched === expectedSet.size;
  };

  await sleep(POLL_FIRST_DELAY_MS);
  if (await runCheck()) return;

  while (true) {
    const elapsed = Date.now() - startTime;
    if (elapsed >= POLL_MAX_DURATION_MS) {
      throw new Error("Students did not finish loading within 5 minutes. Please refresh and try again.");
    }
    const nextDelay = elapsed >= POLL_MID_TO_LATE_MS ? POLL_LATE_DELAY_MS : POLL_MID_DELAY_MS;
    await sleep(nextDelay);
    if (await runCheck()) return;
  }
};

// ---------- Orchestrator ----------

export const runRosterUpload = async ({ file, courseId, gameId, onProgress }) => {
  const report = (stage, extra) => {
    if (onProgress) onProgress({ stage, ...(extra || {}) });
  };

  report("parsing");
  const text = await readFileAsText(file);
  const { rows, error: parseError } = parseRosterCsv(text);
  if (parseError) {
    const err = new Error(parseError);
    err.kind = "validation";
    err.errors = [parseError];
    throw err;
  }
  if (rows.length === 0) {
    const err = new Error("The uploaded file has no student rows.");
    err.kind = "validation";
    err.errors = [err.message];
    throw err;
  }

  report("validating");
  const { errors: validationErrors } = validateRoster(rows);
  if (validationErrors.length) {
    const err = new Error("Please fix the following issues in the file and try again.");
    err.kind = "validation";
    err.errors = validationErrors;
    throw err;
  }

  // Pull the existing teams for this game up front so we know which
  // teams already exist. For those teams we send their competition
  // PK on the CSV's `competition_id` column (BE keeps the assignment
  // as-is). NEW teams use the `competition` column with a small-int
  // NAME instead. `team.competition` from /api/teams/ is a PK, not
  // the competition name, so we can't validate an instructor's
  // explicit Competition value against what's stored. If a CSV row
  // targets an existing team, its explicit Competition is ignored
  // and the team stays where it is.
  let existingTeams = [];
  try {
    const teamsRes = await getRequest(`${API}teams/?game=${Auth.getGameId()}`);
    existingTeams = Array.isArray(teamsRes.body) ? teamsRes.body : [];
  } catch (err) {
    existingTeams = [];
  }
  const existingCompetitionIdByTeamName = {};
  existingTeams.forEach(team => {
    if (team && team.name != null && team.competition != null) {
      existingCompetitionIdByTeamName[String(team.name)] = Number(team.competition);
    }
  });

  const explicitByTeam = {};
  rows.forEach(r => {
    const t = r.team;
    const c = (r.competition || "").trim();
    if (t && c && explicitByTeam[t] == null) {
      explicitByTeam[t] = Number(c);
    }
  });

  report("checking_existing");
  let existingMembers = [];
  try {
    existingMembers = await fetchAllPages(`${API}v2/games/coursemembers/?page_size=500`);
  } catch (err) {
    existingMembers = [];
  }
  const existingUserKeys = new Set(
    existingMembers
      .map(m => extractCourseMemberUserKey(m).toLowerCase())
      .filter(Boolean)
  );

  // The BE identifies users by the stored username (returned as
  // `cm.user` on /v2/games/coursemembers/). A CSV row matches an
  // existing member when EITHER of its possible identifiers lines
  // up with one of those keys:
  //   - the row's explicit username (if provided), OR
  //   - the row's email, which the FE falls back to as the username
  //     when the CSV didn't supply one.
  // Checking both lets a re-upload skip cleanly whether the first
  // upload used an explicit username or defaulted to email, and lets
  // mixed-source rosters (some rows new-style, some old-style) work.
  //
  // A FE-side "username collision" throw was intentionally dropped:
  // when a provided username matches an existing user key we can't
  // tell from this payload whether it's the same person (re-upload)
  // or a different one trying to claim an existing username — the BE
  // owns that uniqueness check and will reject on /upload-students/
  // when it's a real conflict.
  const resolveRowUserKey = (r) => (
    ((r.username || "").trim() || (r.email || "")).toLowerCase()
  );
  const rowMatchesExistingMember = (r) => {
    const candidates = [
      (r.username || "").trim().toLowerCase(),
      (r.email || "").toLowerCase(),
    ].filter(Boolean);
    return candidates.some(k => existingUserKeys.has(k));
  };

  const newRows = rows.filter(r => !rowMatchesExistingMember(r));
  const skippedCount = rows.length - newRows.length;

  if (newRows.length === 0) {
    // Every row is already a course member — nothing to POST, nothing to
    // poll for. Step 1 is effectively complete.
    report("polling", { matched: rows.length, total: rows.length });
  } else {
    report("uploading_students", { newCount: newRows.length, skippedCount });
    const studentCsv = buildStudentCsv(newRows);
    await postFile(
      `${API}courses/${courseId}/upload-students/`,
      studentCsv,
      "students.csv"
    );

    report("polling", { matched: skippedCount, total: rows.length });
    // Poll by resolved username (CSV username || email) because that's
    // the key the BE stores on `cm.user`. Using raw emails here would
    // never match for rows uploaded with an explicit username.
    const expectedUserKeys = rows.map(r => resolveRowUserKey(r));
    await pollCourseMembers({
      courseId,
      expectedUserKeys,
      onProgress: ({ matched, total }) => report("polling", { matched, total }),
    });
  }

  report("resolving_roles");
  let teamPlayers = [];
  try {
    teamPlayers = await fetchAllPages(`${API}v2/games/teamplayers/?page_size=500&game=${Auth.getGameId()}`);
  } catch (err) {
    teamPlayers = [];
  }
  const roleByEmail = {};
  teamPlayers.forEach(tp => {
    const email = extractTeamPlayerEmail(tp).toLowerCase();
    if (email && tp.role) roleByEmail[email] = tp.role;
  });

  const uniqueTeams = [];
  const seenTeams = new Set();
  rows.forEach(r => {
    if (r.team && !seenTeams.has(r.team)) {
      seenTeams.add(r.team);
      uniqueTeams.push(r.team);
    }
  });

  // Split each unique team into one of two buckets:
  //   - competitionIdByTeam: existing teams. Value is the DB PK; goes
  //     into the `competition_id` CSV column. BE reuses the assignment.
  //   - competitionByTeam: new teams. Value is a small-int NAME (from
  //     the instructor's CSV or auto-assigned); goes into the
  //     `competition` CSV column. BE creates the competition if new.
  // A team never populates both columns.
  const teamsNeedingAutoAssign = uniqueTeams.filter(t =>
    existingCompetitionIdByTeamName[t] == null && explicitByTeam[t] == null
  );
  const { assignments: autoAssignByTeam } = determineCompetitions(teamsNeedingAutoAssign);
  const competitionIdByTeam = {};
  const competitionByTeam = {};
  uniqueTeams.forEach(t => {
    if (existingCompetitionIdByTeamName[t] != null) {
      competitionIdByTeam[t] = existingCompetitionIdByTeamName[t];
    } else if (explicitByTeam[t] != null) {
      competitionByTeam[t] = explicitByTeam[t];
    } else {
      competitionByTeam[t] = autoAssignByTeam[t];
    }
  });
  // Report the count of distinct competitions this upload touches
  // (existing PKs + new-team NAMEs).
  const numCompetitions = new Set([
    ...Object.values(competitionIdByTeam).map(v => `id:${v}`),
    ...Object.values(competitionByTeam).map(v => `name:${v}`),
  ]).size;

  report("uploading_game", { numCompetitions });
  const gameCsv = buildGameCsv(rows, roleByEmail, competitionByTeam, competitionIdByTeam);
  await postFile(
    `${API}v2/games/upload/`,
    gameCsv,
    "game.csv",
    { game_id: gameId }
  );

  const addedCount = newRows.length;
  const numTeams = uniqueTeams.length;
  report("success", { addedCount, skippedCount, totalCount: rows.length, numTeams, numCompetitions });
  return { addedCount, skippedCount, totalCount: rows.length, numTeams, numCompetitions };
};



// WEBPACK FOOTER //
// ./src/instructor/utils/roster-upload.js